"use client"

import { cn } from "../lib/utils"
import type { AssessmentStimulus } from "../schema"
import { HTMLContent } from "./shared/html-content"

// ============================================================================
// Types
// ============================================================================

/**
 * Props for the QTIStimulusRenderer component.
 *
 * This is a simple, display-only component for rendering QTI Assessment Stimuli
 * (reading materials, passages, articles).
 * It does not handle interactions.
 */
export type QTIStimulusRendererProps = {
	/**
	 * The parsed stimulus to render.
	 * Use `parseAssessmentStimulusXml()` to create this from raw XML.
	 */
	stimulus: AssessmentStimulus

	/**
	 * Additional CSS class for the container.
	 */
	className?: string
}

// ============================================================================
// Component
// ============================================================================

/**
 * Renders a QTI 3.0 Assessment Stimulus (reading material).
 *
 * Assessment Stimuli are standalone content (articles, passages, etc.)
 * that provide context for assessment items. This component renders
 * the stimulus body HTML with proper support for:
 *
 * - Standard HTML elements (headings, lists, paragraphs, tables)
 * - Images with responsive sizing
 * - MathML mathematical expressions
 * - Figures and figcaptions
 * - Links and text formatting
 * - Collapsible details/summary sections
 *
 * @example
 * ```tsx
 * import { parseAssessmentStimulusXml, QTIStimulusRenderer } from "@superbuilders/incept-renderer"
 *
 * const stimulus = parseAssessmentStimulusXml(xmlString)
 * <QTIStimulusRenderer stimulus={stimulus} className="my-8" />
 * ```
 */
export function QTIStimulusRenderer({ stimulus, className }: QTIStimulusRendererProps) {
	return (
		<article className={cn("qti-stimulus", className)} lang={stimulus.xmlLang} data-stimulus-id={stimulus.identifier}>
			<HTMLContent html={stimulus.bodyHtml} className="qti-stimulus-body" />
		</article>
	)
}

export default QTIStimulusRenderer
